/**
 * Dispute Detail Modal
 * Shows full dispute info with admin actions (resolve, unban, reactivate)
 */

import { LoadingSpinner } from '@components/ui';
import type { AdminDisputeDetail } from '@hooks/useAdminDisputes';
import { AlertTriangle, Ban, CheckCircle, RefreshCw, ShieldOff, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_STATUS_STYLES,
  formatAdminDate,
  formatCents,
} from './utils/adminFormatters';

// =============================================================================
// Types
// =============================================================================

interface DisputeDetailModalProps {
  dispute: AdminDisputeDetail | null;
  isLoading: boolean;
  onClose: () => void;
  onResolve: (
    disputeId: string,
    status: 'won' | 'lost',
    notes: string,
  ) => Promise<{ success: boolean; error?: string }>;
  onUnban: (disputeId: string) => Promise<{ success: boolean; error?: string }>;
  onReactivate: (disputeId: string) => Promise<{ success: boolean; error?: string }>;
}

// =============================================================================
// Component
// =============================================================================

export function DisputeDetailModal({
  dispute,
  isLoading,
  onClose,
  onResolve,
  onUnban,
  onReactivate,
}: DisputeDetailModalProps): ReactElement {
  const [resolveNotes, setResolveNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const clearMessages = () => {
    setActionSuccess(null);
    setActionError(null);
  };

  const handleResolve = async (status: 'won' | 'lost') => {
    if (!dispute) return;
    clearMessages();
    setActionLoading(true);
    const result = await onResolve(dispute.id, status, resolveNotes);
    setActionLoading(false);
    if (result.success) {
      setActionSuccess(`Dispute marked as ${status}`);
      setResolveNotes('');
    } else {
      setActionError(result.error ?? 'Failed to resolve dispute');
    }
  };

  const handleUnban = async () => {
    if (!dispute) return;
    clearMessages();
    setActionLoading(true);
    const result = await onUnban(dispute.id);
    setActionLoading(false);
    if (result.success) {
      setActionSuccess('User unbanned successfully');
    } else {
      setActionError(result.error ?? 'Failed to unban user');
    }
  };

  const handleReactivate = async () => {
    if (!dispute) return;
    clearMessages();
    setActionLoading(true);
    const result = await onReactivate(dispute.id);
    setActionLoading(false);
    if (result.success) {
      setActionSuccess('Subscription reactivated');
    } else {
      setActionError(result.error ?? 'Failed to reactivate subscription');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-theme-text">Dispute Details</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-theme-border text-theme-text-secondary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Issue 3: Loading spinner is reachable because modal opens before data loads */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : !dispute ? (
            <div className="text-center py-12 text-theme-text-tertiary">
              Failed to load dispute details
            </div>
          ) : (
            <>
              {/* Status/Action Messages */}
              {actionSuccess && (
                <div className="p-3 bg-theme-success/10 border border-theme-success rounded-lg text-theme-success flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">{actionSuccess}</span>
                </div>
              )}
              {actionError && (
                <div className="p-3 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{actionError}</span>
                </div>
              )}

              {/* Dispute Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-theme-text-tertiary">User</span>
                  <p className="text-theme-text font-medium">{dispute.userDisplayName}</p>
                  <p className="text-xs text-theme-text-secondary">{dispute.userEmail}</p>
                </div>
                <div>
                  <span className="text-xs text-theme-text-tertiary">Status</span>
                  <p>
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${DISPUTE_STATUS_STYLES[dispute.status] ?? ''}`}
                    >
                      {DISPUTE_STATUS_LABELS[dispute.status] ?? dispute.status}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-xs text-theme-text-tertiary">Amount</span>
                  <p className="text-theme-text font-mono">{formatCents(dispute.amountCents)}</p>
                </div>
                <div>
                  <span className="text-xs text-theme-text-tertiary">Filed</span>
                  <p className="text-sm text-theme-text">{formatAdminDate(dispute.createdAt)}</p>
                </div>
                {dispute.resolvedAt && (
                  <div>
                    <span className="text-xs text-theme-text-tertiary">Resolved</span>
                    <p className="text-sm text-theme-text">{formatAdminDate(dispute.resolvedAt)}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-theme-text-tertiary">User Role</span>
                  <p className="text-sm text-theme-text capitalize">{dispute.userRole}</p>
                </div>
                {dispute.subscription?.status && (
                  <div>
                    <span className="text-xs text-theme-text-tertiary">Subscription</span>
                    <p className="text-sm text-theme-text capitalize">
                      {dispute.subscription?.status}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-theme-text-tertiary">Banned</span>
                  <p className="text-sm">
                    {dispute.disputeBanned ? (
                      <span className="text-red-500 flex items-center gap-1">
                        <Ban className="w-3.5 h-3.5" /> Yes
                      </span>
                    ) : (
                      <span className="text-theme-text-secondary">No</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Reason */}
              <div>
                <span className="text-xs text-theme-text-tertiary">Reason</span>
                <p className="text-theme-text whitespace-pre-wrap mt-1 text-sm bg-theme-bg rounded-lg p-3 border border-theme-border">
                  {dispute.reasonCode}
                </p>
              </div>

              {/* Stripe IDs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-theme-text-tertiary">Stripe Dispute ID</span>
                  <p className="text-xs text-theme-text-secondary font-mono">
                    {dispute.stripeDisputeId}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-theme-text-tertiary">Total User Disputes</span>
                  <p className="text-sm text-theme-text">{dispute.totalUserDisputes}</p>
                </div>
              </div>

              {/* Admin Actions */}
              {dispute.status === 'open' && (
                <div className="pt-4 border-t border-theme-border space-y-4">
                  <span className="text-sm text-theme-text-secondary font-medium">
                    Resolve Dispute
                  </span>

                  <textarea
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    placeholder="Resolution notes (optional)..."
                    className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text text-sm resize-none"
                    rows={3}
                  />

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleResolve('won')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 font-medium text-sm disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      User Wins
                    </button>
                    <button
                      type="button"
                      onClick={() => handleResolve('lost')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 font-medium text-sm disabled:opacity-50"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      User Loses
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Actions (always available) */}
              <div className="flex gap-2 pt-2">
                {dispute.disputeBanned && (
                  <button
                    type="button"
                    onClick={handleUnban}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-orange-500/10 text-orange-500 hover:bg-orange-500/20 disabled:opacity-50"
                  >
                    <ShieldOff className="w-3.5 h-3.5" />
                    Unban User
                  </button>
                )}
                {dispute.subscription?.status && dispute.subscription?.status !== 'active' && (
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 disabled:opacity-50"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reactivate Subscription
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
